// lib/plans.js

export const PLANS = {
    FREE: 'Free',
    PREMIUM: 'Premium',
    PRO: 'Pro',
    BASIC: 'Basic'
};

export const PRODUCTS = {
    BASIC: import.meta.env.VITE_STRIPE_PRODUCT_ID,
    PRO: import.meta.env.VITE_STRIPE_PRODUCT_ID_2,
    PREMIUM: import.meta.env.VITE_STRIPE_PRODUCT_ID_3
};

export const getUserPlan = (user) => {
    // Add debugging for development environment
    if (import.meta.env.DEV) {
        console.group('🔍 getUserPlan Debug Info');
        console.log('User metadata:', user?.unsafeMetadata);
        console.log('isPremium:', user?.unsafeMetadata?.isPremium);
        console.log('Plan object:', user?.unsafeMetadata?.plan);
        console.log('Product ID from metadata:', user?.unsafeMetadata?.plan?.product_id);
        console.log('Available product IDs:', {
            BASIC: PRODUCTS.BASIC,
            PRO: PRODUCTS.PRO,
            PREMIUM: PRODUCTS.PREMIUM
        });
        console.log('Subscription status:', user?.unsafeMetadata?.subscriptionStatus);
        console.log('Subscription history:', user?.unsafeMetadata?.subscriptionHistory);
        console.groupEnd();
    }

    // Early returns for non-premium users
    if (!user?.unsafeMetadata?.isPremium) {
        if (import.meta.env.DEV) console.log('⚠️ Returning FREE plan because isPremium is falsy');
        return PLANS.FREE;
    }

    // Check if there's a more recent active subscription in the history
    // This handles the case where an old subscription is canceled but a new one is active
    const subscriptionHistory = user?.unsafeMetadata?.subscriptionHistory || [];
    const hasRecentActiveSubscription = subscriptionHistory.some(entry => {
        // Check if there's a recent entry with 'active' status and 'premium_enabled' action
        return entry.status === 'active' && entry.action === 'premium_enabled';
    });

    // Check if subscription is active or if there's a recent active subscription in history
    const subscriptionStatus = user?.unsafeMetadata?.subscriptionStatus;
    if (subscriptionStatus && 
        subscriptionStatus !== 'active' && 
        subscriptionStatus !== 'trialing' && 
        !hasRecentActiveSubscription) {
        if (import.meta.env.DEV) {
            console.log(`⚠️ Checking subscription status: ${subscriptionStatus}`);
            console.log(`⚠️ Has recent active subscription: ${hasRecentActiveSubscription}`);
            console.log(`⚠️ Returning FREE plan because subscription status is ${subscriptionStatus} and no recent active subscription found`);
        }
        return PLANS.FREE;
    }

    const clerkPlan = user.unsafeMetadata.plan?.product_id;
    
    if (import.meta.env.DEV) {
        console.log('🔍 Checking product ID:', clerkPlan);
        
        // Check if product ID matches any known product
        const matchesBasic = clerkPlan === PRODUCTS.BASIC;
        const matchesPro = clerkPlan === PRODUCTS.PRO;
        const matchesPremium = clerkPlan === PRODUCTS.PREMIUM;
        
        console.log('Product ID matches:', {
            BASIC: matchesBasic,
            PRO: matchesPro,
            PREMIUM: matchesPremium,
            anyMatch: matchesBasic || matchesPro || matchesPremium
        });
    }

    // Map product IDs to plans
    const planMap = {
        [PRODUCTS.BASIC]: PLANS.BASIC,
        [PRODUCTS.PRO]: PLANS.PRO,
        [PRODUCTS.PREMIUM]: PLANS.PREMIUM
    };

    // Return mapped plan or default to FREE
    const detectedPlan = planMap[clerkPlan] || PLANS.FREE;
    
    if (import.meta.env.DEV) {
        console.log(`🏆 Detected plan: ${detectedPlan}`);
        if (detectedPlan === PLANS.FREE && user?.unsafeMetadata?.isPremium) {
            console.warn('⚠️ User has isPremium=true but no matching product ID was found');
        }
    }
    
    return detectedPlan;
};

// Helper function to check if user has any premium status
export const hasPremiumStatus = (user) => {
    return user?.unsafeMetadata?.isPremium === true;
};

// Helper to check if a plan is at least a certain tier
export const isPlanAtLeast = (currentPlan, minimumPlan) => {
    const planLevels = {
        [PLANS.FREE]: 0,
        [PLANS.BASIC]: 1,
        [PLANS.PRO]: 2,
        [PLANS.PREMIUM]: 3
    };

    return planLevels[currentPlan] >= planLevels[minimumPlan];
};

// Helper to get plan display names (useful for UI)
export const getPlanDisplayName = (plan) => {
    const displayNames = {
        [PLANS.FREE]: "Free",
        [PLANS.BASIC]: "Basic",
        [PLANS.PRO]: "Pro",
        [PLANS.PREMIUM]: "Premium"
    };

    return displayNames[plan] || displayNames[PLANS.FREE];
};